import React, { useEffect, useRef, useState } from "react";
import type { ManifestAsset, ManifestTaxonomy } from "../../types.js";
import { getIdToken } from "../../admin/auth.js";

interface Props {
  taxonomy: ManifestTaxonomy;
  /** Existing album paths, for the folder datalist. */
  albumPaths: string[];
  onClose: () => void;
  onAdded: (asset: ManifestAsset) => void;
}

type Phase = "idle" | "uploading" | "done" | "error";

interface MetaFields {
  name: string;
  sku: string;
  region: string;
  department: string;
  productCategory: string;
  assetType: string;
  albumPath: string;
}

const EMPTY: MetaFields = {
  name: "",
  sku: "",
  region: "",
  department: "",
  productCategory: "",
  assetType: "",
  albumPath: "",
};

const MAX_BYTES = 60 * 1024 * 1024;

export function AddAssetModal({ taxonomy, albumPaths, onClose, onAdded }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fields, setFields] = useState<MetaFields>(EMPTY);
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [dragover, setDragover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const busy = phase === "uploading";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  // Revoke the object URL when the preview changes or the modal unmounts.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function chooseFile(f: File | undefined | null) {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("Please choose an image file (JPEG, PNG, or WebP).");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("Image is larger than 60 MB.");
      return;
    }
    setError(null);
    setFile(f);
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(f);
    });
    // Default the name from the filename (sans extension) if the field is empty.
    setFields((prev) => (prev.name ? prev : { ...prev, name: stripExt(f.name) }));
  }

  function setField(k: keyof MetaFields, v: string) {
    setFields((prev) => ({ ...prev, [k]: v }));
  }

  async function submit() {
    if (!file) {
      setError("Choose an image first.");
      return;
    }
    setPhase("uploading");
    setError(null);
    try {
      const idToken = await getIdToken();
      const buf = await file.arrayBuffer();
      const meta = Object.fromEntries(
        Object.entries(fields).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v),
      );
      const res = await fetch("/admin/asset", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/octet-stream",
          "X-Filename": sanitizeHeader(file.name),
          "X-Asset-Metadata": utf8ToBase64(JSON.stringify(meta)),
        },
        body: buf,
      });
      const json = (await res.json().catch(() => ({}))) as { ok?: boolean; asset?: ManifestAsset; error?: string };
      if (!res.ok || !json.ok || !json.asset) {
        throw new Error(json.error || `Upload failed (${res.status})`);
      }
      onAdded(json.asset);
      setPhase("done");
    } catch (e) {
      setError((e as Error).message);
      setPhase("error");
    }
  }

  if (phase === "done") {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal add-asset-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <header>
            <h2>Asset added</h2>
            <button className="close" aria-label="Close" onClick={onClose}>×</button>
          </header>
          <div className="modal-body">
            <p className="add-done">
              <strong>{fields.name || file?.name}</strong> was added and is live in the library now.
            </p>
          </div>
          <footer className="bulk-footer">
            <button
              className="bulk-btn"
              onClick={() => {
                // Reset for another add without closing.
                setFile(null);
                setPreviewUrl((old) => {
                  if (old) URL.revokeObjectURL(old);
                  return null;
                });
                setFields(EMPTY);
                setPhase("idle");
              }}
            >
              Add another
            </button>
            <button className="bulk-btn bulk-btn-primary" onClick={onClose}>Done</button>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="modal add-asset-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-busy={busy}>
        <header>
          <h2>Add asset</h2>
          <button className="close" aria-label="Close" onClick={onClose} disabled={busy}>×</button>
        </header>
        <div className="modal-body">
          <div
            className={`add-drop ${dragover ? "add-drop-over" : ""} ${previewUrl ? "add-drop-has" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragover(true);
            }}
            onDragLeave={() => setDragover(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragover(false);
              chooseFile(e.dataTransfer.files?.[0]);
            }}
          >
            {previewUrl ? (
              <img className="add-preview" src={previewUrl} alt="preview" />
            ) : (
              <div className="add-drop-hint">
                <strong>Drop an image here</strong>
                <span>or click to browse — JPEG, PNG, or WebP, up to 60 MB</span>
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => chooseFile(e.target.files?.[0])}
            />
          </div>
          {file && (
            <p className="add-filemeta">
              {file.name} · {formatBytes(file.size)}
            </p>
          )}

          <div className="add-form">
            <label className="add-field add-field-wide">
              <span>Name</span>
              <input value={fields.name} onChange={(e) => setField("name", e.target.value)} placeholder="Display name" />
            </label>
            <label className="add-field">
              <span>SKU</span>
              <input value={fields.sku} onChange={(e) => setField("sku", e.target.value)} placeholder="optional" />
            </label>
            <SelectField label="Region" value={fields.region} options={taxonomy.regions} onChange={(v) => setField("region", v)} />
            <SelectField label="Department" value={fields.department} options={taxonomy.departments} onChange={(v) => setField("department", v)} />
            <SelectField label="Product Category" value={fields.productCategory} options={taxonomy.productCategories} onChange={(v) => setField("productCategory", v)} />
            <SelectField label="Asset Type" value={fields.assetType} options={taxonomy.assetTypes} onChange={(v) => setField("assetType", v)} />
            <label className="add-field add-field-wide">
              <span>Folder</span>
              <input
                value={fields.albumPath}
                onChange={(e) => setField("albumPath", e.target.value)}
                placeholder="e.g. Reusable Respirators / 8000 Series"
                list="add-album-paths"
              />
              <datalist id="add-album-paths">
                {albumPaths.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </label>
          </div>

          {error && <p className="add-error">{error}</p>}
        </div>
        <footer className="bulk-footer">
          <button className="bulk-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="bulk-btn bulk-btn-primary" onClick={() => void submit()} disabled={busy || !file}>
            {busy ? "Uploading…" : "Add to library"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="add-field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">(none)</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function stripExt(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

function sanitizeHeader(s: string): string {
  // Header values must be ASCII; strip anything that isn't.
   
  return s.replace(/[^\x20-\x7E]/g, "_");
}

function utf8ToBase64(s: string): string {
  return btoa(unescape(encodeURIComponent(s)));
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
